import type { platform } from '@platform/contracts';

export const TIER_LABEL: Record<platform.Tier, string> = { pool: 'Pool', silo: 'Dedicated cell' };

/** Feature keys are the contract's closed list; shown as given, sentence case, comma-joined. */
export function featureList(features: readonly string[]): string {
  return features.length ? features.join(', ') : 'None';
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const formatNumber = (n: number): string => new Intl.NumberFormat().format(n);

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Millionths of a US dollar as money to the cent (design standard DS-COST-01). */
export function dollars(micro: number): string {
  return USD.format(micro / 1_000_000);
}

/** A dollar amount typed by staff → millionths of a dollar; undefined unless a number ≥ 0. */
export function microFromDollars(text: string): number | undefined {
  const trimmed = text.trim().replace(/^\$/, '').replaceAll(',', '');
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return undefined;
  return Math.round(Number(trimmed) * 1_000_000);
}

/** Millionths of a dollar → the plain number staff type ("20" or "2.5"). */
export function dollarsInput(micro: number | undefined): string {
  return micro === undefined ? '' : String(micro / 1_000_000);
}

/** The assistant's model classes, named by purpose (never by vendor). */
export const MODEL_LABEL: Record<platform.AssistantModel, string> = {
  fast: 'Fast',
  standard: 'Standard',
};

/** One tenant's assistant totals over a period, from the control plane's metering. */
export interface AssistantTotals {
  readonly spentMicroUsd: number;
  readonly calls: number;
  readonly inputTokens: number;
  readonly outputTokens: number;
}

export function assistantTotals(usage: platform.MeteringUsage | undefined): AssistantTotals {
  const metric = (name: string) => usage?.metrics.find((m) => m.metric === name);
  return {
    spentMicroUsd: metric('assistant.cost')?.quantity ?? 0,
    calls: metric('assistant.cost')?.records ?? 0,
    inputTokens: metric('assistant.input-tokens')?.quantity ?? 0,
    outputTokens: metric('assistant.output-tokens')?.quantity ?? 0,
  };
}

/** The UTC month and day the provider bills by (DS-COST-08). */
export function utcPeriods(now = new Date()): { month: string; day: string } {
  const iso = now.toISOString();
  return { month: iso.slice(0, 7), day: iso.slice(0, 10) };
}

export type SpendState = 'ok' | 'warning' | 'stopped' | 'unlimited';

/** Fixed thresholds on every spend bar: warning from 80%, stopped at 100% (DS-COST-02). */
export function spendState(spent: number, limit: number | undefined): SpendState {
  if (limit === undefined) return 'unlimited';
  if (spent >= limit) return 'stopped';
  return spent * 100 >= limit * 80 ? 'warning' : 'ok';
}

/**
 * When a limit's UTC period starts again, in the viewer's own time. Assistant limits reset at 00:00
 * UTC (the provider's billing day); people are shown that moment where they are (owner ruling
 * 2026-11-03, design standard DS-CONTENT-04): "Resets at 7:00 PM your time".
 */
export function resetsAtLocal(
  every: 'day' | 'month',
  now = new Date(),
  options: { locale?: string; timeZone?: string } = {},
): string {
  const next =
    every === 'day'
      ? new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1))
      : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  const zone = options.timeZone ? { timeZone: options.timeZone } : {};
  const time = new Intl.DateTimeFormat(options.locale, {
    hour: 'numeric',
    minute: '2-digit',
    ...zone,
  }).format(next);
  if (every === 'day') return `Resets at ${time} your time`;
  const date = new Intl.DateTimeFormat(options.locale, {
    month: 'short',
    day: 'numeric',
    ...zone,
  }).format(next);
  return `Resets ${date} at ${time} your time`;
}
